// widgets/chart/ui/ChartTooltip.tsx
import { FC, CSSProperties } from "react";
import {
    Box,
    TooltipDataCol,
    TooltipDataColRow,
    TooltipDataColTitle,
    TooltipDataColValue,
    TooltipDateRow,
} from "widgets/chart/ui/styles";

export type TooltipPoint = { ts: number; value: number | null };
export type TooltipTextItem = { title: string; dim: string; isSign: boolean };
export type TooltipData =
    | { type: "column"; current: TooltipPoint; prev?: TooltipPoint; tooltipText: TooltipTextItem[] }
    | { type: "sparkline"; current: TooltipPoint; tooltipText: TooltipTextItem[] }
    | { type: "column2"; current1: TooltipPoint; current2: TooltipPoint; tooltipText: TooltipTextItem[] };

type Props = {
    data: TooltipData;
    tooltipText: TooltipTextItem[];
    style?: CSSProperties & { ["--arrow-x"]?: string };
};

const formatDate = (ts: number) =>
    new Date(ts)
        .toLocaleString("ru-RU", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" })
        .replace(" в", " в");

const formatValue = (v: number | null, tooltipItem?: TooltipTextItem): string => {
    if (v == null) return "—";
    
    // Определяем тип графика по title или dim
    const title = tooltipItem?.title?.toLowerCase() || "";
    const dim = tooltipItem?.dim || "";
    
    // Температура: 1 знак после запятой
    if (title.includes("температура") || dim === "°C") {
        return v.toFixed(1);
    }
    
    // CO2 и влажность: 0 знаков после запятой (полное округление до целого)
    if (title.includes("co₂") || title.includes("co2") || dim === "ppm" || 
        title.includes("влажность") || dim === "%") {
        return Math.round(v).toString();
    }
    // По умолчанию возвращаем как есть (число в строку)
    return String(v.toFixed(2));
};

const formatSigned = (v: number, withSign: boolean) => {
    if (!withSign) return `${v}`;
    if (v > 0) return `+${v}`;
    if (v < 0) return `${v}`;
    return "0";
};

const rounded = (n: number, tooltipItem?: TooltipTextItem) => {
    // Определяем тип графика для правильного округления
    const title = tooltipItem?.title?.toLowerCase() || "";
    const dim = tooltipItem?.dim || "";
    
    // Температура: 1 знак после запятой
    if (title.includes("температура") || dim === "°C") {
        return Number(n.toFixed(1));
    }
    
    // CO2 и влажность: 0 знаков после запятой (полное округление до целого)
    if (title.includes("co₂") || title.includes("co2") || dim === "ppm" ||
        title.includes("влажность") || dim === "%") {
        return Math.round(n);
    }
    
    // По умолчанию: 2 знака после запятой
    return Number(n.toFixed(2));
};

export const ChartTooltip: FC<Props> = ({ data, style, tooltipText }) => {
    return (
        <Box style={style}>
            {data.type === "column" && (
                <>
                    <TooltipDateRow>{formatDate(data.current.ts)}</TooltipDateRow>
                    <TooltipDataColRow>
                        <TooltipDataCol>
                            <TooltipDataColTitle>{tooltipText[0]?.title}</TooltipDataColTitle>
                            <TooltipDataColValue>
                                <strong>{formatValue(data.current.value, tooltipText[0])}</strong>
                                <span>{tooltipText[0]?.dim}</span>
                            </TooltipDataColValue>
                        </TooltipDataCol>
                        {tooltipText[1] &&
                            data.prev &&
                            data.current.value != null &&
                            data.prev.value != null && (
                                <TooltipDataCol>
                                    <TooltipDataColTitle>{tooltipText[1].title}</TooltipDataColTitle>
                                    <TooltipDataColValue>
                                        <strong>{formatSigned(rounded(data.current.value - data.prev.value, tooltipText[0]), tooltipText[1].isSign)}</strong>
                                        <span>{tooltipText[1].dim}</span>
                                    </TooltipDataColValue>
                                </TooltipDataCol>
                            )}
                    </TooltipDataColRow>
                </>
            )}

            {data.type === "sparkline" && (
                <>
                    <TooltipDateRow>{formatDate(data.current.ts)}</TooltipDateRow>
                    <TooltipDataCol>
                        <TooltipDataColTitle>{tooltipText[0]?.title}</TooltipDataColTitle>
                        <TooltipDataColValue>
                            <strong>{formatValue(data.current.value, tooltipText[0])}</strong>
                            <span>{tooltipText[0]?.dim}</span>
                        </TooltipDataColValue>
                    </TooltipDataCol>
                </>
            )}

            {data.type === "column2" && (
                <>
                    <TooltipDateRow>{formatDate(data.current1.ts)}</TooltipDateRow>
                    <TooltipDataColRow>
                        <TooltipDataCol>
                            <TooltipDataColTitle>{tooltipText[0]?.title}</TooltipDataColTitle>
                            <TooltipDataColValue>
                                <strong>{formatValue(data.current1.value, tooltipText[0])}</strong>
                                <span>{tooltipText[0]?.dim}</span>
                            </TooltipDataColValue>
                        </TooltipDataCol>
                        <TooltipDataCol>
                            <TooltipDataColTitle>{tooltipText[1]?.title}</TooltipDataColTitle>
                            <TooltipDataColValue>
                                <strong>{formatValue(data.current2.value, tooltipText[1])}</strong>
                                <span>{tooltipText[1]?.dim}</span>
                            </TooltipDataColValue>
                        </TooltipDataCol>
                    </TooltipDataColRow>
                </>
            )}
        </Box>
    );
};
